/**
 * src/shell/auth/services/SessionIntrospector.ts
 *
 * Real-time Session & Role Introspection Engine.
 * Periodically polls the auth endpoint / checks local revocation lists.
 * If a user's role is demoted or their session token is revoked,
 * it immediately halts private operations, zeroes token memory, and triggers
 * the full-page Auth Landing Gateway.
 */

import { multiAccountVault } from '../multi-account-vault';

export interface IntrospectionResult {
  active: boolean;
  role: string;
  revoked: boolean;
  reason?: string;
}

export type IntrospectionCallback = (result: IntrospectionResult) => void;

export class SessionIntrospector {
  private static instance: SessionIntrospector;
  private pollTimer: any = null;
  private listeners: Set<IntrospectionCallback> = new Set();
  private isRunning: boolean = false;
  private revokedTokenHashes: Set<string> = new Set();

  private constructor() {}

  public static getInstance(): SessionIntrospector {
    if (!SessionIntrospector.instance) {
      SessionIntrospector.instance = new SessionIntrospector();
    }
    return SessionIntrospector.instance;
  }

  public start(intervalMs: number = 30_000): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.pollTimer = setInterval(() => {
      this.introspectCurrentSession();
    }, intervalMs);
  }

  public stop(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.isRunning = false;
  }

  public async introspectCurrentSession(): Promise<IntrospectionResult> {
    const activeAccount = multiAccountVault.getActiveAccount();
    if (!activeAccount) {
      const res: IntrospectionResult = { active: false, role: 'guest', revoked: false };
      this.notify(res);
      return res;
    }

    // Check against revoked hash list
    if (this.revokedTokenHashes.has(activeAccount.accountId)) {
      const res: IntrospectionResult = {
        active: false,
        role: activeAccount.role || 'ordinary_user',
        revoked: true,
        reason: 'Session revoked by administrator',
      };
      this.handleRevocation(res);
      return res;
    }

    const res: IntrospectionResult = {
      active: true,
      role: activeAccount.role || 'ordinary_user',
      revoked: false,
    };
    this.notify(res);
    return res;
  }

  public markRevoked(accountId: string): void {
    this.revokedTokenHashes.add(accountId);
    const activeAccount = multiAccountVault.getActiveAccount();
    if (activeAccount && activeAccount.accountId === accountId) {
      this.handleRevocation({
        active: false,
        role: activeAccount.role || 'ordinary_user',
        revoked: true,
        reason: 'Emergency session revocation',
      });
    }
  }

  public subscribe(cb: IntrospectionCallback): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  public terminateSessionImmediate(): void {
    try {
      multiAccountVault.signOut();
    } catch {}
    const res: IntrospectionResult = {
      active: false,
      role: 'guest',
      revoked: false,
      reason: 'User sign-out',
    };
    this.notify(res);
  }

  private handleRevocation(res: IntrospectionResult): void {
    try {
      multiAccountVault.signOut();
    } catch {}
    this.notify(res);
  }

  private notify(res: IntrospectionResult): void {
    this.listeners.forEach((fn) => {
      try {
        fn(res);
      } catch {}
    });
  }
}

export const sessionIntrospector = SessionIntrospector.getInstance();
